import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, softwareAppSchema, faqSchema } from '../utils/structuredData';
import { CameraIcon, SparklesIcon, UploadIcon, PaletteIcon, DownloadIcon, ArrowRightIcon, ExternalLinkIcon, CheckCircle2Icon, StarIcon, WandIcon, LayoutGridIcon, TrendingUpIcon, DollarSignIcon, RocketIcon } from 'lucide-react';
const SHOPIFY_URL = 'https://apps.shopify.com/ai-product-photoshoot-dfd';
function InstallButton({
  size = 'md',
  variant = 'solid'
}: {
  size?: 'md' | 'lg';
  variant?: 'solid' | 'white';
}) {
  const pad = size === 'lg' ? 'px-10 py-5 text-lg' : 'px-8 py-4 text-base';
  const color = variant === 'white' ? 'bg-white text-black hover:bg-gray-200 shadow-[0_0_30px_rgba(255,255,255,0.2)]' : 'bg-purple-600 text-white hover:bg-purple-500 shadow-[0_0_30px_rgba(147,51,234,0.3)]';
  return <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 ${color} ${pad} rounded-full font-bold transition-all duration-300 hover:scale-105`} data-id="element-587" data-mp="24kb89ahkwpx9">
      Install Free on Shopify <ExternalLinkIcon className="w-5 h-5" data-id="element-588" data-mp="mg8dkgjd3iwj" />
    </a>;
}
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-589" data-mp="mil3pgz9wzko">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-purple-400 fill-purple-400" data-id="element-590" data-mp="1f4xfuiw5lu9v" data-mpi={i} />)}
    </div>;
}
function AppFrame({
  title,
  children
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return <div className="glass-card border-white/10 p-2 rounded-2xl relative shadow-2xl shadow-black/50" data-id="element-591" data-mp="15fe19pfh4mkt">
      <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-592" data-mp="1adntps9td92q">
        <div className="h-10 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-593" data-mp="1y3rulrvmauq1">
          <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-594" data-mp="22ui2bbrin9nw" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-595" data-mp="22vt4j75bc9cw" />
          <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-596" data-mp="2cpfj1p8uslep" />
          {title && <span className="ml-3 text-xs text-gray-500 font-medium" data-id="element-597" data-mp="27r48tdenc8vy">
              {title}
            </span>}
        </div>
        <div className="p-6" data-id="element-598" data-mp="22qpzls1b6swc">{children}</div>
      </div>
    </div>;
}
interface FeatureBlock {
  eyebrow: string;
  title: string;
  desc: string;
  bullets: string[];
  mockup: React.ReactNode;
}
const FEATURE_BLOCKS: FeatureBlock[] = [{
  eyebrow: 'AI Scene Studio',
  title: 'Turn plain product shots into scroll-stopping scenes',
  desc: 'Upload any product photo and our AI drops it into a beautiful, realistic lifestyle scene, like marble countertops, sunlit studios, or cozy interiors, in seconds. No photographer, no studio, no props.',
  bullets: ['Realistic AI lifestyle scenes from a single product photo', 'Describe any scene in plain words, or pick a preset', 'Studio-quality results without a studio'],
  mockup: <div className="space-y-4" data-id="element-599" data-mp="3hq6q3i3qcrgi">
        <div className="flex items-center gap-4" data-id="element-600" data-mp="3hovpts9hu1u5">
          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/10 text-center" data-id="element-601" data-mp="3mnysj6203vyy">
            <div className="aspect-square bg-white rounded-lg mb-3 overflow-hidden" data-id="element-602" data-mp="3qqbo51n1adi0">
              <img src="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=800&auto=format&fit=crop" alt="Plain product photo of a cosmetic jar" className="w-full h-full object-cover grayscale contrast-90 brightness-105" data-id="element-603" data-mp="3vp6c0hzuys8z" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400" data-id="element-604" data-mp="37z2acg68d2km">
              Original
            </span>
          </div>
          <div className="shrink-0 flex flex-col items-center" data-id="element-605" data-mp="3crlp3ggnm3r0">
            <SparklesIcon className="w-7 h-7 text-purple-400 animate-pulse mb-1" data-id="element-606" data-mp="2f9vejoy5ur32" />
            <ArrowRightIcon className="w-5 h-5 text-gray-600" data-id="element-607" data-mp="2k9honpiozr7n" />
          </div>
          <div className="flex-1 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30 text-center relative overflow-hidden" data-id="element-608" data-mp="qragfu6wb0ad">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-50" data-id="element-609" data-mp="lt8vt4ttmsws" />
            <div className="aspect-square rounded-lg mb-3 overflow-hidden border border-purple-500/20 relative z-10" data-id="element-610" data-mp="zke0wmfykdei">
              <img src="https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=800&auto=format&fit=crop" alt="Same cosmetic jar styled in an AI lifestyle scene" className="w-full h-full object-cover saturate-125" data-id="element-611" data-mp="urkulu4ucx0b" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-purple-300 relative z-10" data-id="element-612" data-mp="1igchc5znwn5p">
              AI Lifestyle
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded-lg p-2.5" data-id="element-613" data-mp="3kabqakpbasf8">
          <WandIcon className="w-4 h-4" data-id="element-614" data-mp="3649shrkfy6ud" /> “Place on a sunlit marble kitchen
          counter”
        </div>
      </div>
}, {
  eyebrow: 'Preset Categories',
  title: '20+ ready-made scene templates',
  desc: 'Not sure where to start? Choose from a growing library of professionally designed scene presets tailored to your product category, including skincare, apparel, food, home goods and more.',
  bullets: ['20+ curated scene presets across product categories', 'One tap to generate on-brand imagery', 'Fresh templates added regularly'],
  mockup: <div className="space-y-3" data-id="element-615" data-mp="2dfm1dfd0gn8w">
        <div className="flex items-center justify-between mb-1" data-id="element-616" data-mp="2dge0h05th33x">
          <span className="text-sm font-semibold text-white" data-id="element-617" data-mp="2iep8d5cjx3ji">
            Scene Presets
          </span>
          <span className="text-xs text-purple-300" data-id="element-618" data-mp="bo97rmghu1s5">20+ themes</span>
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-619" data-mp="bmeosdos8jqp">
          {[{
        cat: 'Skincare',
        img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=400&auto=format&fit=crop'
      }, {
        cat: 'Apparel',
        img: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=400&auto=format&fit=crop'
      }, {
        cat: 'Food',
        img: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=400&auto=format&fit=crop'
      }, {
        cat: 'Home',
        img: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=400&auto=format&fit=crop'
      }, {
        cat: 'Jewelry',
        img: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=400&auto=format&fit=crop'
      }, {
        cat: 'Outdoor',
        img: 'https://images.unsplash.com/photo-1533873984035-25970ab07461?q=80&w=400&auto=format&fit=crop'
      }].map(({
        cat,
        img
      }) => <div key={cat} className="rounded-lg border border-white/10 bg-white/5 p-3 text-center hover:border-purple-500/30 transition-colors" data-id="element-620" data-mp="1j8dm5euqop63">
              <div className="aspect-square rounded-md overflow-hidden mb-2 relative" data-id="element-621" data-mp="1t61keerk0et0">
                <img src={img} alt={`${cat} scene preset`} className="w-full h-full object-cover" data-id="element-622" data-mp="1y4utluzt5jnd" />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent" data-id="element-623" data-mp="2266g3rndxorc" />
              </div>
              <span className="text-[11px] text-gray-300" data-id="element-624" data-mp="2bw9fqap0i0yn">{cat}</span>
            </div>)}
        </div>
      </div>
}, {
  eyebrow: 'One-Click Publishing',
  title: 'Straight to your store, no downloads, no re-uploads',
  desc: 'Love a result? Publish it directly to the product in your Shopify catalog with a single click. Your storefront updates instantly. No file juggling, no manual uploads.',
  bullets: ['Publish generated images right to your Shopify products', 'Pulls products directly from your catalog', 'Instant storefront updates'],
  mockup: <div className="space-y-3" data-id="element-625" data-mp="3l4wu6c9gn6c0">
        <span className="text-sm font-semibold text-white" data-id="element-626" data-mp="3l3u60c3ivhzs">
          Publish to Catalog
        </span>
        {[{
      name: 'Hydrating Face Serum',
      status: 'Published',
      img: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=200&auto=format&fit=crop'
    }, {
      name: 'Linen Summer Dress',
      status: 'Published',
      img: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=200&auto=format&fit=crop'
    }, {
      name: 'Ceramic Coffee Mug',
      status: 'Publishing…',
      img: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?q=80&w=200&auto=format&fit=crop'
    }].map(p => <div key={p.name} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-id="element-627" data-mp="k8crq3ykyhqc">
            <div className="w-9 h-9 rounded-md overflow-hidden border border-purple-500/20 shrink-0" data-id="element-628" data-mp="p0w5b0x9pcv5">
              <img src={p.img} alt={p.name} className="w-full h-full object-cover" data-id="element-629" data-mp="b63g1arksibd" />
            </div>
            <span className="flex-1 text-xs text-gray-300 truncate" data-id="element-630" data-mp="130f6f9rnvnxy">
              {p.name}
            </span>
            <span className={`text-[11px] ${p.status === 'Published' ? 'text-purple-300' : 'text-gray-500'}`} data-id="element-631" data-mp="17xx8qhb6b4wz">
              {p.status}
            </span>
          </div>)}
      </div>
}, {
  eyebrow: 'Gallery & Export',
  title: 'Every asset saved, ready to reuse',
  desc: 'All your generated images live in a permanent in-app gallery, so you can reuse them for ads, email, and social anytime. Export in high resolution for any channel.',
  bullets: ['Permanent gallery of every generated image', 'High-resolution export for ads & social', 'Reuse assets across all your marketing'],
  mockup: <div className="space-y-3" data-id="element-632" data-mp="7j3bdg2a5fvi">
        <div className="flex items-center justify-between" data-id="element-633" data-mp="7i1omebwj4xy">
          <span className="text-sm font-semibold text-white" data-id="element-634" data-mp="cglakeqhwgqb">Your Gallery</span>
          <span className="text-xs text-purple-300 flex items-center gap-1" data-id="element-635" data-mp="3tprsyl0vc45l">
            <DownloadIcon className="w-3.5 h-3.5" data-id="element-636" data-mp="3trurxuk017mg" /> Hi-res export
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2" data-id="element-637" data-mp="2gugge5uk3qr">
          {['https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1533873984035-25970ab07461?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=300&auto=format&fit=crop', 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?q=80&w=300&auto=format&fit=crop'].map((img, i) => <div key={i} className="aspect-square rounded-md overflow-hidden border border-purple-500/20 relative" data-id="element-638" data-mp="2bqtwwioq4cgo" data-mpi={i}>
              <img src={img} alt={`Generated lifestyle image ${i + 1}`} className="w-full h-full object-cover" data-id="element-639" data-mp="2lq2urmmpmb8b" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/15 to-transparent" data-id="element-640" data-mp="vfpvax5n1die" />
            </div>)}
        </div>
      </div>
}];
const STATS = [{
  value: '20+',
  label: 'Scene presets'
}, {
  value: '< 30s',
  label: 'Per image'
}, {
  value: '$0',
  label: 'To get started'
}, {
  value: '4.9★',
  label: 'Merchant rating'
}];
const OUTCOMES = [{
  icon: <TrendingUpIcon className="w-7 h-7 text-purple-400" data-id="element-641" data-mp="35dsogn0ss7s3" />,
  title: 'Convert more',
  desc: 'Lifestyle imagery helps shoppers picture the product in their lives, and that drives more add-to-carts.'
}, {
  icon: <DollarSignIcon className="w-7 h-7 text-purple-400" data-id="element-642" data-mp="3edz3weg23htw" />,
  title: 'Save thousands',
  desc: 'Skip expensive photographers, studios, and prop rentals. Get studio-grade visuals for free.'
}, {
  icon: <RocketIcon className="w-7 h-7 text-purple-400" data-id="element-643" data-mp="3o3iktek7l1m4" />,
  title: 'Ship faster',
  desc: 'Launch products and campaigns the same day, with no waiting weeks for a photoshoot.'
}];
const TESTIMONIALS = [{
  quote: 'We replaced an entire photoshoot budget with this app. Our product pages have never looked better and conversions are up.',
  name: 'Megan T.',
  role: 'DTC Skincare Brand'
}, {
  quote: 'I generated a full season of lifestyle shots in an afternoon. The presets are genuinely good and publishing to Shopify is instant.',
  name: 'Carlos V.',
  role: 'Apparel Store Owner'
}, {
  quote: 'The before/after is night and day. Plain packshots turned into scroll-stopping ads. Free plan got us hooked.',
  name: 'Aisha K.',
  role: 'Home Goods Merchant'
}];
const FAQS = [{
  q: 'Do I need design or photography skills?',
  a: 'Not at all. Pick a product, choose a preset or describe a scene in plain words, and the AI does the rest. Publishing to your store is one click.'
}, {
  q: 'Is it really free?',
  a: 'Yes. There’s a free plan with no credit card required. You can generate images, build your gallery, and publish to your store to see the value first.'
}, {
  q: 'Will the images match my product accurately?',
  a: 'The AI places your actual product photo into the new scene, so your product stays true while the background and styling are transformed.'
}, {
  q: 'Where can I use the generated images?',
  a: 'Anywhere, including product pages, social media, email, and paid ads. Export in high resolution for any channel.'
}];
export function AIPhotoshootPage() {
  usePageSEO({
    canonical: '/ai-photoshoot',
    keywords: 'AI product photography, Shopify AI app, AI photoshoot Shopify, product background generator, ecommerce lifestyle images',
    ogType: 'product',
    jsonLd: [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }, {
      name: 'AI Photoshoot',
      path: '/ai-photoshoot'
    }]), {
      ...softwareAppSchema({
        name: 'AI Product Photoshoot',
        description: 'AI Product Photoshoot turns plain Shopify product photos into high-converting lifestyle images in seconds.',
        path: '/ai-photoshoot',
        category: 'BusinessApplication'
      }),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '86'
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }, faqSchema(FAQS.map(f => ({
      question: f.q,
      answer: f.a
    })))],
    title: 'AI Product Photoshoot for Shopify | Design Flow Digitals',
    description: 'AI Product Photoshoot turns plain Shopify product photos into high-converting lifestyle images in seconds. 20+ scene presets, one-click publishing, free plan. Install today.'
  });
  return <div className="w-full selection:bg-purple-500/30 selection:text-white" data-id="element-644" data-mp="207j5dffyqj4b">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-645" data-mp="1v8zhvalq0z9u">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black pointer-events-none" data-id="element-646" data-mp="svbhl6g76xuv" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" data-id="element-647" data-mp="11u56eanaejxv" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-648" data-mp="16t89vqnmci3q">
          <div data-id="element-649" data-mp="a4aj0jizp4bu">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-purple-500/30 bg-purple-500/10" data-id="element-650" data-mp="a0mn5dq2o5ty">
              <CameraIcon className="w-4 h-4 text-purple-400" data-id="element-651" data-mp="j2vww1dinv6a" />
              <span className="text-sm font-medium text-purple-300" data-id="element-652" data-mp="j1b2cbr7cc8f">
                Live on Shopify App Store
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-653" data-mp="3mkw77n6jocn2">
              Studio-quality product photos, powered by AI
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed" data-id="element-654" data-mp="3rguq4v93t2g3">
              Transform plain Shopify product shots into high-converting
              lifestyle scenes in seconds, with no photographer, no studio, no
              props.
            </p>
            <div className="flex items-center gap-4 mb-10" data-id="element-655" data-mp="39zxm0c2zrw8v">
              <StarRow data-id="element-656" data-mp="360ghplzax55a" />
              <span className="text-sm text-gray-400" data-id="element-657" data-mp="35z5criu4drcl">
                4.9 rating · Free plan available
              </span>
            </div>
            <div className="flex flex-wrap gap-4" data-id="element-658" data-mp="312ncow9qih6l">
              <InstallButton size="lg" variant="white" data-id="element-659" data-mp="2w9c00ei8r66r" />
              <a href="#how-it-works" className="px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-660" data-mp="2rb0tacn8tbsr">
                See how it works
              </a>
            </div>
          </div>

          {/* Hero Mockup */}
          <div className="relative" data-id="element-661" data-mp="2dazpaobolo92">
            <div className="absolute inset-0 bg-purple-500/20 blur-[100px] rounded-full" data-id="element-662" data-mp="2dc1dzvlme999" />
            <div className="relative z-10" data-id="element-663" data-mp="5330ehilh4k2">
              <AppFrame title="AI Photoshoot Studio" data-id="element-664" data-mp="a1mq8t0b70hp">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-665" data-mp="a14jh2g0t3vj">
                  <div className="flex-1 w-full bg-white/5 rounded-xl p-4 border border-white/10 text-center" data-id="element-666" data-mp="3s6pwih20hijp">
                    <div className="aspect-square bg-white rounded-lg mb-4 overflow-hidden" data-id="element-667" data-mp="1nfcpgf2y1o">
                      <img src="https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=800&auto=format&fit=crop" alt="Plain product photo of a serum bottle on a white background" className="w-full h-full object-cover grayscale contrast-90 brightness-105" data-id="element-668" data-mp="3ifv6d1ln9ysz" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-669" data-mp="2zjwulvcvyd84">
                      Before (Plain)
                    </span>
                  </div>
                  <div className="shrink-0 flex flex-col items-center" data-id="element-670" data-mp="2kwy360e13ite">
                    <SparklesIcon className="w-8 h-8 text-purple-500 mb-2 animate-pulse" data-id="element-671" data-mp="2fx1ydyxibncs" />
                    <ArrowRightIcon className="w-6 h-6 text-gray-500 hidden md:block" data-id="element-672" data-mp="2twl28fjaccja" />
                  </div>
                  <div className="flex-1 w-full bg-purple-500/10 rounded-xl p-4 border border-purple-500/30 text-center relative overflow-hidden" data-id="element-673" data-mp="2pugetyjfkc53">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-50" data-id="element-674" data-mp="33swjzl1n9ul4" />
                    <div className="aspect-square rounded-lg mb-4 overflow-hidden border border-purple-500/20 relative z-10" data-id="element-675" data-mp="2yttpuvn7d8rx">
                      <img src="https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?q=80&w=800&auto=format&fit=crop" alt="Same serum bottle styled in a lifestyle scene" className="w-full h-full object-cover saturate-125" data-id="element-676" data-mp="38lplm01tgmsz" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-purple-300 relative z-10" data-id="element-677" data-mp="3u32j00cmai8g">
                      After (Lifestyle)
                    </span>
                  </div>
                </div>
              </AppFrame>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-678" data-mp="3bee2rrp2x130">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-679" data-mp="3bentxilv1eaa">
          {STATS.map(s => <div key={s.label} className="text-center" data-id="element-680" data-mp="3l47fwx4vdo9d">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-681" data-mp="3l5s6ycrz6p2h">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-682" data-mp="3gbeb699eifxa">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-black" data-id="element-683" data-mp="2nplcobv51ngb">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-684" data-mp="2nmpd6j3n6qpr">
          <h2 className="text-4xl font-bold mb-6" data-id="element-685" data-mp="135zgdsub5dau">
            Better visuals. More sales. Less spend.
          </h2>
          <p className="text-xl text-gray-400" data-id="element-686" data-mp="17ysjyde1iflc">
            Great product imagery is the fastest lever for conversion, and now
            it costs you nothing to start.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-687" data-mp="y7xxp90snfzw">
          {OUTCOMES.map(o => <div key={o.title} className="glass-card p-8 border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-688" data-mp="1lv663ztir565">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6 border border-purple-500/20" data-id="element-689" data-mp="1cvsqepesxkcz">
                {o.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-690" data-mp="1h5q6zrxew9q">{o.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-691" data-mp="6g8o809ho287">{o.desc}</p>
            </div>)}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section-padding bg-[#020202] border-y border-white/5" data-id="element-692" data-mp="284wbuxzsz3l8">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-693" data-mp="238xkd1j6h5gl">
          <h2 className="text-4xl font-bold mb-6" data-id="element-694" data-mp="1yebh5ln92j7l">How It Works</h2>
          <p className="text-xl text-gray-400" data-id="element-695" data-mp="1uestii41nu12">
            Generate stunning imagery in three simple steps.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-696" data-mp="1pgrce2q6i4s8">
          {[{
          step: '01',
          title: 'Select Your Product',
          desc: 'Choose any product directly from your Shopify catalog.'
        }, {
          step: '02',
          title: 'Describe Your Scene',
          desc: 'Use custom prompts or choose from 20+ pre-set themes.'
        }, {
          step: '03',
          title: 'Generate & Publish',
          desc: 'AI creates stunning lifestyle images. Publish directly to your store.'
        }].map((item, i) => <div key={i} className="glass-card p-8 border-white/10 relative overflow-hidden group" data-id="element-697" data-mp="wjyqocmu749j" data-mpi={i}>
              <div className="absolute top-0 left-0 w-full h-1 bg-purple-500/50 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" data-id="element-698" data-mp="11dl1s0dcr038" />
              <div className="text-5xl font-bold text-purple-500/20 mb-6" data-id="element-699" data-mp="8o4bb89alt9n">
                {item.step}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-700" data-mp="do05ygw3o8cj">
                {item.title}
              </h3>
              <p className="text-gray-400" data-id="element-701" data-mp="hpu2qe36cs7f">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Detailed Feature Blocks with mockups */}
      <section className="bg-black border-b border-white/5" data-id="element-702" data-mp="1t24nbx7rli0s">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-28" data-id="element-703" data-mp="1y17srirnpq9a">
          {FEATURE_BLOCKS.map((f, i) => <div key={f.title} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-704" data-mp="1wlmyx2sbu0bt" data-mpi={i}>
              <div className={i % 2 === 1 ? 'lg:order-2' : ''} data-id="element-705" data-mp="1rntw8jq8foga">
                <p className="text-sm font-bold text-purple-400 uppercase tracking-widest mb-4" data-id="element-706" data-mp="25mtjbmy3jvac">
                  {f.eyebrow}
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" data-id="element-707" data-mp="2fjfzvzje406k">
                  {f.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-708" data-mp="2ajjxg4qccgt4">
                  {f.desc}
                </p>
                <ul className="space-y-4" data-id="element-709" data-mp="2p8g1lqpa19og">
                  {f.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-710" data-mp="2kaoc13c3tznf">
                      <CheckCircle2Icon className="w-5 h-5 text-purple-400 shrink-0 mt-0.5" data-id="element-711" data-mp="2tb4fwdwrf8st" />
                      <span data-id="element-712" data-mp="3cwmo7uvcsot1">{b}</span>
                    </li>)}
                </ul>
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''} data-id="element-713" data-mp="3hvzd0l5sc5y9">
                <AppFrame data-id="element-714" data-mp="3htd5gliw9qg1">{f.mockup}</AppFrame>
              </div>
            </div>)}
        </div>
      </section>

      {/* Key Features grid */}
      <section className="section-padding bg-black" data-id="element-715" data-mp="37w8fealxhaty">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-716" data-mp="2u64y7y1wo7bd">
          <h2 className="text-4xl font-bold mb-6" data-id="element-717" data-mp="2u4twe3s0t9mw">Everything you get</h2>
          <p className="text-xl text-gray-400" data-id="element-718" data-mp="2yxwr4f8jap4o">
            A complete product-photography studio inside your Shopify admin.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-719" data-mp="2p71ucgxnw74z">
          {[{
          icon: <SparklesIcon className="w-6 h-6 text-purple-400" data-id="element-720" data-mp="2b70un4t4v5r3" />,
          title: 'AI-Powered Generation',
          desc: 'Advanced AI creates realistic lifestyle imagery from your product photos.'
        }, {
          icon: <UploadIcon className="w-6 h-6 text-purple-400" data-id="element-721" data-mp="ls89gk3ozvgr" />,
          title: 'Direct Publishing',
          desc: 'Publish generated images directly to your Shopify products with one click.'
        }, {
          icon: <PaletteIcon className="w-6 h-6 text-purple-400" data-id="element-722" data-mp="7za9ekgb1f6v" />,
          title: 'Custom Scenes',
          desc: 'Describe any scene you want or use our 20+ pre-set product categories.'
        }, {
          icon: <LayoutGridIcon className="w-6 h-6 text-purple-400" data-id="element-723" data-mp="zr7xpqnam8ce" />,
          title: 'Image Gallery',
          desc: 'All generated assets are stored permanently in-app for repeat use.'
        }, {
          icon: <DownloadIcon className="w-6 h-6 text-purple-400" data-id="element-724" data-mp="qrkvx3qnj0it" />,
          title: 'High Resolution Export',
          desc: 'Export files for product pages, social media, and ad campaigns.'
        }, {
          icon: <CheckCircle2Icon className="w-6 h-6 text-purple-400" data-id="element-725" data-mp="1qxydfatjrtnv" />,
          title: 'Free to Start',
          desc: 'Begin on the free plan with no credit card required.'
        }].map((feature, i) => <div key={i} className="glass-card p-8 border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-726" data-mp="1zwju10fgkym0" data-mpi={i}>
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-6 border border-purple-500/20" data-id="element-727" data-mp="29o6b3deh9rwo">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-728" data-mp="2jm3zq2091qpt">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-729" data-mp="2no7f31q1hfgd">
                {feature.desc}
              </p>
            </div>)}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-[#050505] border-y border-white/5" data-id="element-730" data-mp="t94rj9kjw7g3">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-731" data-mp="adfom1qguxt4">
          <h2 className="text-4xl font-bold mb-6" data-id="element-732" data-mp="abus46kld4sm">Perfect For</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-733" data-mp="5d9ri61fgj3n">
          {['Product Pages', 'Social Media Content', 'Ad Campaigns', 'Seasonal Promotions'].map((useCase, i) => <div key={i} className="glass-card p-6 border-white/10 flex items-center gap-4" data-id="element-734" data-mp="3njo6m8bfmz3m" data-mpi={i}>
              <CheckCircle2Icon className="w-6 h-6 text-purple-500 shrink-0" data-id="element-735" data-mp="3ilwf3fep9ezy" />
              <span className="text-lg font-medium text-white" data-id="element-736" data-mp="frkwmavufyt">{useCase}</span>
            </div>)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-black" data-id="element-737" data-mp="3rnvxoc5d4r50">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-738" data-mp="1btdkl1edbzyl">
          <h2 className="text-4xl font-bold mb-6" data-id="element-739" data-mp="16vkdm658r1x9">
            Merchants love the results
          </h2>
          <p className="text-xl text-gray-400" data-id="element-740" data-mp="1fcknm8672h21">
            Join Shopify stores creating scroll-stopping visuals without a
            studio.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-741" data-mp="1jbthre55cgy4">
          {TESTIMONIALS.map(t => <div key={t.name} className="glass-card p-8 border-white/10" data-id="element-742" data-mp="1aczp1yuxsapu">
              <StarRow data-id="element-743" data-mp="1afc6maws8j2d" />
              <p className="text-gray-300 leading-relaxed my-6" data-id="element-744" data-mp="1y32f69faj6lt">“{t.quote}”</p>
              <div data-id="element-745" data-mp="1y56gqkppvtix">
                <p className="font-bold text-white" data-id="element-746" data-mp="231ne85it3k8o">{t.name}</p>
                <p className="text-sm text-gray-500" data-id="element-747" data-mp="231406zwa1fzx">{t.role}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding bg-[#020202] border-t border-white/5" data-id="element-748" data-mp="1tb29e0cjhjhb">
        <div className="max-w-md mx-auto" data-id="element-749" data-mp="2h16ao1299755">
          <div className="glass-card p-10 border-purple-500/30 bg-gradient-to-b from-purple-900/10 to-transparent text-center relative overflow-hidden" data-id="element-750" data-mp="2h0dc3ndg9ny3">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600" data-id="element-751" data-mp="3nsxfcvoh139z" />
            <h3 className="text-3xl font-bold text-white mb-4" data-id="element-752" data-mp="3sqqhndmnld4z">
              Free Plan Available
            </h3>
            <p className="text-gray-400 mb-8" data-id="element-753" data-mp="1e5m51jtc8po">
              Get started with our free plan. No credit card required.
            </p>
            <ul className="space-y-4 mb-10 text-left" data-id="element-754" data-mp="6e0b2zk12o83">
              {['AI image generation', 'Product catalog integration', 'Permanent image gallery', 'Direct publishing to store'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-755" data-mp="3eq62qsjv01gr" data-mpi={i}>
                  <CheckCircle2Icon className="w-5 h-5 text-purple-400 shrink-0" data-id="element-756" data-mp="3iu3z4vhsuy64" />{' '}
                  {f}
                </li>)}
            </ul>
            <InstallButton data-id="element-757" data-mp="y2tq4typ1vg3" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-black border-t border-white/5" data-id="element-758" data-mp="2il4jffdwl1vi">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-759" data-mp="2in8ldrs8rqt5">
          <h2 className="text-4xl font-bold mb-6" data-id="element-760" data-mp="2ekltjqvkm5vy">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-761" data-mp="29r9fgaoiphhg">
          {FAQS.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-762" data-mp="24sy6s39yld0t">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-763" data-mp="1zv63ye7s06hu">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-764" data-mp="1uvjnzx0kcc5c">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-purple-900/20" data-id="element-765" data-mp="1quzr8ujwjajq">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-766" data-mp="1lw6pfzdz97ze">
          Ready to transform your product photography?
        </h2>
        <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto" data-id="element-767" data-mp="3l5wq6u90rnyy">
          Install AI Product Photoshoot and create your first lifestyle scene in
          under a minute.
        </p>
        <div className="flex items-center justify-center gap-3 mb-10" data-id="element-768" data-mp="36u3zjatqpkx">
          <StarRow data-id="element-769" data-mp="7zvl7xyi717o" />
          <span className="text-sm text-gray-400" data-id="element-770" data-mp="80er2gavpz5c">
            4.9 rating · Free plan · No credit card
          </span>
        </div>
        <InstallButton size="lg" data-id="element-771" data-mp="3qb09uc9qmlni" />

        {/* App Details Footer */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-8 text-sm text-gray-500 max-w-3xl mx-auto" data-id="element-772" data-mp="3vc61s7vkq4vz">
          <p data-id="element-773" data-mp="qxg0cdbgulf3">
            Developer: <span className="text-gray-300" data-id="element-774" data-mp="qxzfxndn8xpy">Pankaj Maurya</span>
          </p>
          <p data-id="element-775" data-mp="cy6tw5d2b5ob">
            Works with: <span className="text-gray-300" data-id="element-776" data-mp="cwvhxi9gqrm4">Shopify Admin</span>
          </p>
          <p data-id="element-777" data-mp="hwi5akj09o8n">
            Language: <span className="text-gray-300" data-id="element-778" data-mp="hvyjutu5chpv">English</span>
          </p>
        </div>
      </section>
    </div>;
}